import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";

import { Button } from "@whoosh/design-system/src/components/controls";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { HomeTabsNavigatorParamList } from "../../../../navigators/HomeTabsNavigator/types";
import { useMutationWithError } from "../../../../utils/hooks/useMutationWithError";
import { NotificationExample } from "./NotificationExample";
import { NotificationsNudgeModalMutation } from "./__generated__/NotificationsNudgeModalMutation.graphql";

type NotificationNudgeModalProps = {
  userId: string;
  isOpen: boolean;
  setNudgeModal: (open: boolean) => void;
  isNewFlow: boolean;
};

export const NotificationsNudgeModal = ({
  userId,
  isOpen,
  setNudgeModal,
  isNewFlow,
}: NotificationNudgeModalProps) => {
  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const [setSeenNudgeModal, isSetting] =
    useMutationWithError<NotificationsNudgeModalMutation>(
      NotificationsNudgeModalMutationString
    );

  return (
    <StandardModal
      disableContentScroll
      isOpen={isOpen}
      onClose={() => {
        setSeenNudgeModal({
          variables: {
            input: {
              userId,
            },
          },
          onCompleted: () => {
            setNudgeModal(false);
            if (!isNewFlow) {
              navigation.navigate("MainHome");
            }
          },
        });
      }}
      backgroundColorOverride="surface-main-primary-subtle"
      content={
        <VStack flex={1} height="100%" p={6}>
          <VStack
            height="100%"
            alignItems={"center"}
            justifyContent={"space-between"}
            flex={1}
          >
            <Box />
            <NotificationExample />
            <VStack gap={4}>
              <Text
                variant="mainTitle2"
                textAlign={"center"}
              >{t`Get a heads up on all your activity at the club.`}</Text>
              <Text
                variant="mainSmallMedium"
                textAlign={"center"}
              >{t`Get booking updates and reminders
              when you turn on notifications`}</Text>
            </VStack>
            <Box height={56} width="100%" mt={4}>
              <Button
                accessibilityLabel={t`Manage notifications`}
                testID="notification-nudge-modal-button"
                onPress={() => {
                  setSeenNudgeModal({
                    variables: {
                      input: {
                        userId,
                      },
                    },
                    onCompleted: () => {
                      navigation.navigate("Profile", {
                        screen: "ProfileHome",
                        params: { openNotificationSettings: true },
                      });
                      setNudgeModal(false);
                    },
                  });
                }}
                variant="main"
                width="100%"
                isLoading={isSetting}
              >
                {t`Manage notifications`}
              </Button>
            </Box>
          </VStack>
        </VStack>
      }
    />
  );
};

export const NotificationsNudgeModalMutationString = graphql`
  mutation NotificationsNudgeModalMutation(
    $input: SetNotificationNudgeModalSeenInput!
  ) {
    responsePayload: setNotificationNudgeModalSeen(input: $input) {
      success
      errors {
        code

        message
        field
      }
      user {
        id
        ... on MemberUser {
          metadata {
            hasSeenNotificationNudgeModal
          }
        }
      }
    }
  }
`;
